import React from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import {
  deriveMembershipFromUser,
  getMembershipConfig,
  getMembershipTooltipLines,
} from '../lib/customerMembershipStatus';

/**
 * Icon badge with hover tooltip for customer subscription/trial state.
 */
export default function CustomerMembershipBadge({
  customer,
  size = 'sm',
  showLabel = false,
  className = '',
}) {
  const c = deriveMembershipFromUser(customer || {});
  const cfg = getMembershipConfig(c);
  const { Icon } = cfg;
  const lines = getMembershipTooltipLines(c);

  const iconSize = size === 'lg' ? 'w-5 h-5' : 'w-4 h-4';
  const pad = size === 'lg' ? 'px-2.5 py-1' : 'px-1.5 py-0.5';

  const badge = (
    <span
      className={[
        'inline-flex items-center gap-1 rounded-full border font-medium shrink-0',
        pad,
        cfg.bgClass,
        size === 'lg' ? 'text-sm' : 'text-xs',
        className,
      ].join(' ')}
      data-testid={`membership-badge-${c.membership_status}`}
    >
      <Icon className={[iconSize, cfg.iconClass].join(' ')} aria-hidden />
      {showLabel && (
        <span className={cfg.iconClass}>{cfg.shortLabel}</span>
      )}
    </span>
  );

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="inline-flex rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            aria-label={c.membership_label || 'Membership status'}
          >
            {badge}
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="max-w-xs bg-slate-900 text-slate-50 px-3 py-2 text-left"
        >
          <div className="font-semibold mb-1">{c.membership_label}</div>
          <ul className="space-y-0.5 text-[11px] opacity-90">
            {lines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
